import { HTMLAttributes, useEffect, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import mergeRefs from 'merge-refs'

import HeroVideo from '@/components/base/hero-video'
import Media, { MediaElement } from '@/components/base/media'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { useLenis } from '@/contexts/lenis'
import { useWindowSizeOnWidthChange } from '@/hooks/useWindowSizeOnWidthChange'
import { useLoaderStore } from '@/store/loader'
import { MediaQuery } from '@/constants'

import { heroRevealAnimation } from './animations'
import { cn } from '@/lib/utils'

export type HeroProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    media: MediaElement
    mask: { media: MediaElement }
    overline: string
    use_mask: boolean
  }>

const Hero = ({ media, mask, use_mask, overline, ...props }: HeroProps) => {
  const root = useRef<HTMLDivElement>(null!)
  const { lenis } = useLenis()
  const setLoaderState = useLoaderStore((state) => state.setLoaderState)

  const { width } = useWindowSizeOnWidthChange()

  useGSAP(
    (context) => {
      if (!lenis) return

      const matchMedia = gsap.matchMedia(root.current)

      matchMedia.add(
        {
          isSmall: MediaQuery.SMALL,
          isLarge: MediaQuery.LARGE
        },
        ({ conditions }) => {
          if (conditions?.isSmall) return

          const documentElement = document.documentElement
          const selector =
            context?.selector || gsap.utils.selector(root.current)

          const logo = selector('[data-motion-logo]')
          const text = selector('[data-motion-text]')
          const bgmask = selector('[data-motion-bgmask]')
          const background = selector('[data-motion-background]')

          if (lenis.limit === 0) {
            return gsap.delayedCall(0.8, () =>
              documentElement.classList.add('ui-reveal')
            )
          }

          const scrollTriggerOptions = {
            trigger: root.current,
            start: '100% 100%',
            horizontal: true,
            markers: false,
            onLeave: () => documentElement.classList.add('ui-reveal'),
            onEnterBack: () => documentElement.classList.remove('ui-reveal')
          }

          const timeline = use_mask
            ? ScrollTrigger.create(scrollTriggerOptions)
            : gsap
                .timeline({
                  scrollTrigger: {
                    ...scrollTriggerOptions,
                    scrub: true,
                    pin: true,
                    pinSpacing: true
                  }
                })
                .to(logo, {
                  keyframes: [
                    {
                      scale: 3,
                      duration: 2,
                      ease: 'expoScale(1,2,power2.out)'
                    },
                    {
                      xPercent: -100,
                      duration: 4,
                      delay: -1,
                      ease: 'none'
                    }
                  ]
                })
                .to(
                  text,
                  {
                    yPercent: 100,
                    duration: 0.2,
                    ease: 'none'
                  },
                  0
                )
                .from(
                  bgmask,
                  {
                    xPercent: 100,
                    duration: 3,
                    ease: 'none'
                  },
                  1
                )
                .to(
                  background,
                  {
                    xPercent: -50,
                    duration: 4,
                    ease: 'none'
                  },
                  1
                )

          return () => {
            timeline.kill()
          }
        }
      )

      return () => {
        matchMedia.revert()
      }
    },
    {
      scope: root,
      dependencies: [use_mask, lenis, width],
      revertOnUpdate: true
    }
  )

  useEffect(() => {
    if (!media?.use_video) {
      setLoaderState({ heroCanPlay: true })
      setLoaderState({ isPosterReady: true })
    }
  }, [media, setLoaderState])

  return (
    <Motion animation={heroRevealAnimation}>
      {(motionRef) => (
        <Section ref={mergeRefs(root, motionRef)} {...props}>
          <div
            className={cn('relative min-h-[100lvh] w-full bg-dark lg:w-screen')}
          >
            <div
              className={cn(
                'absolute left-0 top-0 h-full w-full',
                'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/45'
              )}
              data-motion-background
            >
              {media?.use_video && media.video?.url ? (
                <HeroVideo
                  className="absolute left-0 top-0 h-full w-full object-cover will-change-transform"
                  onCanPlay={() => setLoaderState({ heroCanPlay: true })}
                  onPosterReady={() => setLoaderState({ isPosterReady: true })}
                  canPlay={true}
                  video={{ url: media.video?.url }}
                  // @ts-ignore
                  poster={{ url: media.video_poster?.url }}
                  data-motion-videobg
                />
              ) : (
                <Media fill fit="cover" {...media} data-motion-videobg />
              )}
            </div>

            {!use_mask ? (
              <div
                className="absolute left-0 top-0 hidden h-full w-full bg-dark lg:block"
                data-motion-bgmask
              />
            ) : null}

            <div
              className="absolute left-1/2 top-1/2 h-auto w-full -translate-x-1/2 -translate-y-1/2 px-[2rem] md:px-[3rem] lg:px-[5rem]"
              data-motion-logo-wrap
            >
              <div
                className="relative aspect-[1330/330] h-full w-full"
                data-motion-logo
              >
                <div className="absolute left-0 top-0 h-full w-full bg-black [clip-path:url(#mask)]">
                  {use_mask ? (
                    <Media
                      fill
                      fit="cover"
                      {...mask?.media}
                      data-motion-videomask
                    />
                  ) : null}
                </div>
              </div>

              <div className="absolute -bottom-[3.5rem] left-0 flex w-full justify-center overflow-hidden md:-bottom-[4.5rem]">
                <Typography
                  className="flex text-[1.28rem] font-bold capitalize tracking-[0.0384rem]"
                  data-motion-text
                >
                  {overline}
                </Typography>
              </div>
            </div>

            <svg width="0" height="0" viewBox="0 0 1330 330">
              <defs>
                <clipPath id="mask" clipPathUnits="objectBoundingBox">
                  <path
                    d="M43.1505 254.181V75.7723C43.1505 43.5858 34.2508 27.0314 0.358124 16.0106L2.06429 4.15967C27.9334 6.28084 49.099 7.11087 62.6561 7.11087C76.2131 7.11087 108.446 6.28084 132.609 4.98969C213.121 0.747346 256.375 29.1526 256.375 80.4297C256.375 117.735 231.797 144.434 180.935 163.939L227.554 235.552C262.738 289.78 278.416 300.801 306.36 313.113L299.997 324.964C273.298 322.843 247.014 322.843 228.799 324.964C211.415 284.293 185.131 235.552 142.753 175.79L81.2855 192.298V254.181C81.2855 286.368 90.1852 302.507 124.078 313.943L122.372 325.794C101.621 323.673 76.167 322.843 62.6099 322.843C49.0529 322.843 27.8412 323.673 2.01817 325.794L0.312012 313.943C34.2047 302.922 43.1044 286.414 43.1044 254.181H43.1505ZM122.418 163.939C182.18 148.261 209.709 117.735 209.709 83.427C209.709 29.1987 160.138 17.3017 81.2855 24.0802V174.96L122.418 163.939Z M958.899 254.181V75.7723C958.899 43.5858 949.999 27.0314 916.106 16.0106L917.812 4.15967C943.682 6.28084 964.847 7.11087 978.404 7.11087C991.961 7.11087 1017.42 6.28084 1038.17 4.15967L1039.87 16.0106C1005.98 27.4465 997.08 43.5397 997.08 75.7723V254.181C997.08 286.368 1005.98 302.507 1039.87 313.943L1038.17 325.794C1017.42 323.673 991.961 322.843 978.404 322.843C964.847 322.843 943.635 323.673 917.812 325.794L916.106 313.943C949.999 302.922 958.899 286.414 958.899 254.181Z M1223.77 312.237C1266.97 312.237 1293.26 293.147 1293.26 266.909C1293.26 234.261 1265.73 211.804 1194.07 180.863C1127.95 152.458 1100.42 121.516 1100.42 80.4299C1100.42 27.4467 1153.4 0.747559 1204.26 0.747559C1245.39 0.747559 1255.12 11.7684 1309.35 11.7684C1305.94 46.5372 1306.4 77.4787 1310.18 111.786L1300.45 113.908C1279.7 42.7099 1236.86 17.7169 1198.73 17.7169C1167.37 17.7169 1137.72 36.3464 1137.72 63.5066C1137.72 96.5692 1166.13 119.441 1227.55 147.016C1301.28 180.079 1329.69 205.072 1329.69 248.325C1329.69 296.236 1294.92 329.253 1211.87 329.253C1174.98 329.253 1137.72 325.84 1093.22 311.868C1102.95 287.29 1104.66 235.137 1098.71 206.778L1109.32 203.366C1135.19 285.584 1179.27 312.283 1223.77 312.283V312.237Z M802.945 150.521C836.469 138.209 858.557 114.046 858.557 75.8184C858.557 28.7376 825.955 2.73015 745.259 4.52854C706.432 5.40467 567.449 6.64971 485.553 6.64971C424.869 6.64971 391.76 5.81969 367.136 4.11353L352.703 111.463H364.6C384.567 52.4855 420.627 23.6191 474.071 23.6191C517.786 23.6191 535.17 42.2946 535.17 79.6457V153.887H493.162C462.589 153.887 438.011 142.866 429.065 102.518H418.46C417.168 151.304 412.926 186.534 403.196 229.419L413.802 233.246C433.769 183.168 455.396 170.856 488.919 170.856H535.17V249.801C535.17 287.152 517.786 305.827 469.829 305.827C406.609 305.827 374.653 282.494 353.441 192.529H341.544L353.118 325.333C377.742 323.627 410.851 322.797 485.507 322.797C567.403 322.797 712.058 325.241 761.352 325.748C841.956 326.624 883.135 293.93 883.135 240.855C883.135 192.068 853.023 161.08 802.945 150.474V150.521ZM655.708 254.089C655.708 285.768 648.515 310.485 614.53 310.485C580.545 310.485 573.351 285.768 573.351 254.089V75.4495C573.351 43.7702 579.115 19.054 614.53 19.054C649.944 19.054 655.708 43.7702 655.708 75.4495V254.089ZM693.889 23.6652C772.834 16.8867 813.966 28.7376 813.966 83.0581C813.966 117.412 796.997 142.912 735.022 149.276L693.843 153.518V23.6652H693.889ZM763.058 308.825C715.977 308.825 693.889 279.128 693.889 234.122V170.902L740.555 166.245C808.018 159.466 838.59 193.405 838.59 242.607C838.59 287.152 808.018 308.825 763.058 308.825Z"
                    transform={`scale(${1 / 1330}, ${1 / 330})`}
                  />
                </clipPath>
              </defs>
            </svg>
          </div>
        </Section>
      )}
    </Motion>
  )
}

export default Hero
